import { useLocation, matchPath } from 'react-router-dom';

import { Box, List } from '@mui/material';
import { styled } from '@mui/material/styles';

import SidebarMenuItem from './item';
import { TMenuCategories } from 'src/types/MenuItems/menuItem.model';
import useMenuItem from 'src/hooks/MenuItems/useMenuItem';

const MenuWrapper = styled(List)(
  ({ theme }) => `
    padding: 0;

    .MuiListSubheader-root {
      text-transform: uppercase;
      font-weight: bold;
      font-size: ${theme.typography.pxToRem(12)};
      color: ${theme.sidebar.menuItemHeadingColor};
      padding: ${theme.spacing(0.8, 2)};
      line-height: 1.4;
    }
`
);

const SubMenuWrapper = styled(List)(
  ({ theme }) => `
    &.MuiList-root {
      padding: 0;

      .MuiList-root .MuiList-root .MuiListItem-root .MuiButton-root {
        font-weight: normal !important;
      }

      .MuiListItem-root {
        padding: 2px ${theme.spacing(2)};
    
        .MuiButton-root {
          display: flex;
          color: ${theme.sidebar.menuItemColor};
          background-color: ${theme.sidebar.menuItemBg};
          width: 100%;
          justify-content: flex-start;
          font-size: ${theme.typography.pxToRem(13)};
          padding-top: ${theme.spacing(0.8)};
          padding-bottom: ${theme.spacing(0.8)};
          position: relative;

          .MuiBadge-root {
            position: absolute;
            right: ${theme.spacing(4)};

            .MuiBadge-standard {
              background: ${theme.colors.primary.main};
              font-size: ${theme.typography.pxToRem(9)};
              font-weight: bold;
              text-transform: uppercase;
              color: ${theme.palette.primary.contrastText};
            }
          }
    
          .MuiButton-startIcon,
          .MuiButton-endIcon {
            transition: ${theme.transitions.create(['color'])};

            .MuiSvgIcon-root {
              font-size: inherit;
              transition: none;
            }
          }

          .MuiButton-startIcon {
            font-size: ${theme.typography.pxToRem(26)};
            margin-right: ${theme.spacing(1.5)};
            color: ${theme.sidebar.menuItemIconColor};
          }
          
          .MuiButton-endIcon {
            margin-left: auto;
            font-size: ${theme.typography.pxToRem(22)};
          }

          &.Mui-active,
          &:hover {
            background-color: ${theme.sidebar.menuItemBgActive};
            color: ${theme.sidebar.menuItemColorActive};

            .MuiButton-startIcon,
            .MuiButton-endIcon {
                color: ${theme.sidebar.menuItemIconColorActive};
            }
          }
        }

        &.Mui-children {
          flex-direction: column;
          line-height: 1;
        }

        .MuiCollapse-root {
          width: 100%;

          .MuiList-root {
            padding: ${theme.spacing(1, 0)};
          }

          .MuiListItem-root {
            padding: 1px ${theme.spacing(0)};

            .MuiButton-root {
              font-size: ${theme.typography.pxToRem(13)};
              padding: ${theme.spacing(0.5, 2, 0.5, 6.5)};

              &.Mui-active,
              &:hover {
                color: ${theme.sidebar.menuItemBgActive};
                background-color: ${theme.sidebar.background};

                .MuiButton-startIcon,
                .MuiButton-endIcon {
                    background-color: ${theme.sidebar.menuItemIconColorActive};
                }
              }
            }
          }
        }
      }
    }
`
);

const renderSidebarMenuItems = ({ items, path }: { items: TMenuCategories[]; path: string }): JSX.Element => (
  <SubMenuWrapper>{items.reduce((ev: any, item: any) => reduceChildRoutes({ ev, item, path }), [])}</SubMenuWrapper>
);

const reduceChildRoutes = ({
  ev,
  path,
  item
}: {
  ev: JSX.Element[];
  path: string;
  item: TMenuCategories;
}): Array<JSX.Element> => {
  const key = item.pageName;

  const exactMatch = item.route
    ? !!matchPath(
        {
          path: item.route,
          end: true
        },
        path
      )
    : false;

  if (item.childPages) {
    const partialMatch = item.route
      ? !!matchPath(
          {
            path: item.route,
            end: false
          },
          path
        )
      : false;

    ev.push(
      <SidebarMenuItem
        key={key}
        active={partialMatch}
        open={partialMatch}
        name={item.pageName}
        icon={item.icon}
        link={item.route}
        isParent={item.isParent}
        isModule={item.isModule}
      >
        {renderSidebarMenuItems({
          path,
          items: item.childPages
        })}
      </SidebarMenuItem>
    );
  } else {
    ev.push(
      <SidebarMenuItem
        key={key}
        active={exactMatch}
        name={item.pageName}
        link={item.route}
        icon={item.icon}
        isParent={item.isParent}
        isModule={item.isModule}
      />
    );
  }

  return ev;
};

function SidebarMenu() {
  const location = useLocation();
  const { data } = useMenuItem();

  return (
    <>
      <Box>
        <div className="relative overflow-hidden w-full h-full">
          {data && (
            <MenuWrapper>
              {renderSidebarMenuItems({
                items: data,
                path: location.pathname
              })}
            </MenuWrapper>
          )}
        </div>
      </Box>
    </>
  );
}

export default SidebarMenu;
